/**
 * Helpers for DV-311 monthly compliance messages.
 *
 * A message belongs to a calendar month, sent as "YYYY-MM". Months are judged on the
 * America/Los_Angeles calendar by the backend, so the "current month" here is too.
 * Never turn a period into a Date: `new Date('2026-10')` is UTC midnight on the 1st,
 * which is still September anywhere west of Greenwich.
 */

import { BODY_MAX, isBlankHtml, sanitizeComplianceHtml } from './complianceHtml';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const PERIOD_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
export const TITLE_MAX = 255;
export const LINK_MAX = 500;
const BODY_TOO_LONG = `The message is too long: at most ${BODY_MAX.toLocaleString('en-US')} characters, formatting included`;

/** "2026-10" -> "October 2026". Anything unrecognised is returned as-is. */
export const formatPeriod = (period) => {
  if (!period || !PERIOD_PATTERN.test(period)) return period || '';
  const [year, month] = period.split('-');
  return `${MONTH_NAMES[Number(month) - 1]} ${year}`;
};

/** The current month on the Los Angeles calendar, as "YYYY-MM". */
export const currentPeriod = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const year = parts.find((p) => p.type === 'year').value;
  const month = parts.find((p) => p.type === 'month').value;
  return `${year}-${month}`;
};

/** "2026-12" -> "2027-01". */
export const nextPeriod = (period) => {
  const [year, month] = period.split('-').map(Number);
  return month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`;
};

/** "YYYY-MM" strings sort chronologically as plain strings. */
export const isFuturePeriod = (period, now = new Date()) => period > currentPeriod(now);

/**
 * The message members currently see: the newest PUBLISHED month that is not in the future.
 * Returns its id, or null.
 */
export const liveMessageId = (messages, now = new Date()) => {
  const current = currentPeriod(now);
  const live = (messages || [])
    .filter((m) => m.status === 'PUBLISHED' && m.period <= current)
    .sort((a, b) => (a.period < b.period ? 1 : -1))[0];
  return live ? live.id : null;
};

// Bare-URL linkifying lives in ./linkify so complianceHtml can use it too; re-exported here.
export { splitLinks } from './linkify';

const isBlank = (value) => !value || !value.trim();

/**
 * Whether a message body shows nothing. HTML bodies are judged on their visible text, so an
 * empty editor ("<p></p>") counts as blank; TEXT bodies (and a missing format) as plain text.
 */
export const isBlankBody = (body, bodyFormat) => (bodyFormat === 'HTML' ? isBlankHtml(body) : isBlank(body));

export const hasSpanish = (message) => !isBlank(message?.titleEs)
  && !isBlankBody(message?.bodyEs, message?.bodyFormat);

const tooLong = (html) => sanitizeComplianceHtml(html).length > BODY_MAX;

/**
 * Client-side mirror of the backend rules. Returns { field: message } for each problem; empty
 * when the form can be sent. `requireSpanish` applies when the message is (or is being) published.
 * The form's bodies are editor HTML.
 */
export const validateMessage = (form, { requireSpanish = false } = {}) => {
  const errors = {};
  if (!form.period || !PERIOD_PATTERN.test(form.period)) errors.period = 'Choose a month';
  if (isBlank(form.titleEn)) errors.titleEn = 'English title is required';
  if (isBlankHtml(form.bodyEn)) errors.bodyEn = 'English message is required';
  else if (tooLong(form.bodyEn)) errors.bodyEn = BODY_TOO_LONG;
  if (requireSpanish) {
    if (isBlank(form.titleEs)) errors.titleEs = 'Spanish title is required for a published message';
    if (isBlankHtml(form.bodyEs)) errors.bodyEs = 'Spanish message is required for a published message';
  }
  if (!errors.bodyEs && !isBlankHtml(form.bodyEs) && tooLong(form.bodyEs)) errors.bodyEs = BODY_TOO_LONG;
  if ((form.titleEn || '').trim().length > TITLE_MAX) errors.titleEn = `At most ${TITLE_MAX} characters`;
  if ((form.titleEs || '').trim().length > TITLE_MAX) errors.titleEs = `At most ${TITLE_MAX} characters`;
  const link = (form.linkUrl || '').trim();
  if (link) {
    if (!/^https?:\/\//.test(link)) errors.linkUrl = 'Link must start with http:// or https://';
    else if (link.length > LINK_MAX) errors.linkUrl = `At most ${LINK_MAX} characters`;
  }
  return errors;
};
